import { useState, useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import _ from "lodash"
import ChartProvider, { resultObj } from "./chartProvider"
import {
  CHART_FONTS,
  FontName,
  TOOLS,
  CycleAnaTypesValue,
  MODAL_CANVAS_ID,
} from "./chartConfig"
import { setTools } from "../logic/chartTools"
import useChartCursor from "./useChartCursor"
import {
  changeProviders,
  changeSymbolDetail,
  changeDialog,
  changeDialoPriceVolumeValue,
  chartUnmount,
  symbolDetail,
  pickTarget,
} from "redux/ducks/Chart"
import { setKVOnOff } from "./chartTools"
import { LoadStateType } from "../../../../redux/ducks/PageLoad"
import store from "../../../../redux/store"

interface props {
  canvasId: string
  miniChartFlag?: boolean
}

interface Window {
  spinnerService: any
  Logic: any
  IntelliFactory: any
  Trading: any
}
declare var window: Window

// F#に渡す用。classでバインドしていないと値が変更されたことが検知されない。
class selectedSymbol {
  symbol: symbolDetail
  constructor() {}
  setSymbol = (symbol: symbolDetail) => {
    this.symbol = symbol
  }
  getSymbol = () => {
    return this.symbol
  }
}

class LastClose {
  price: number
  constructor(price: number) {
    this.price = price || 0
  }
  get = () => {
    return this.price
  }
}

const useCprovider = (props: props): Array<any> => {
  const { canvasId, miniChartFlag } = props
  const _selectedSymbol = useMemo(() => {
    return new selectedSymbol()
  }, [])
  // Selector
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const toolsSelector = (state: any) => state.chart[canvasId].tools
  const symbolSelector = (state: any) => state.chart[canvasId].symbol
  const symbolDetailSelector = (state: any) =>
    state.chart[canvasId].symbolDetail
  const chartSizeSelector = (state: any) => state.chart[canvasId].size
  const renderCountSelector = (state: any) => state.chart[canvasId].renderCount
  const cycleAnaTypesSelector = (state: any) =>
    state.chart[canvasId].cycleAnaTypes
  const priceVolumeParamSelector = (state: any) =>
    state.chart[canvasId].priceVolumeParam
  const colorSettingSelector = (state: any) => state.chart.colorSetting
  const providersSelector = (state: any) => state.chart[canvasId].providers
  const tenkanKikanSelector = (state: any) => state.chart[canvasId].tenkanKikan

  const plotType = useSelector(plotTypeSelector)
  const tools = useSelector(toolsSelector)
  const symbol = useSelector(symbolSelector)
  const symbolDetail = useSelector(symbolDetailSelector)
  const renderCount = useSelector(renderCountSelector)
  const size = useSelector(chartSizeSelector)
  const { width, height } = size
  const cycleAnaTypes = useSelector(cycleAnaTypesSelector)
  const priceVolumeParam = useSelector(priceVolumeParamSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const providers = useSelector(providersSelector)
  const tenkanKikan = useSelector(tenkanKikanSelector)
  const [_provider, _controller, _procs, _resetCtx] = providers

  const [fontType] = useState("middle")
  const [prevPrice, setPrevPrice] = useState(0)
  const dispatch = useDispatch()
  let isInfoValid = false
  const lastCloseController = useMemo(() => {
    return new LastClose(_.get(symbolDetail, "LastClose", 0))
  }, [])

  const [mouseValue, cursor] = useChartCursor({
    canvasID: props.canvasId,
    isInfoValid: isInfoValid,
    miniChartFlag: !!miniChartFlag,
  })

  // F#に渡す関数。returnの値を更新する必要があるためトップレベルに定義し、レンダー毎に再定義する。
  const _cProc = window.Trading.Chart.CycleAnalysis.CycleAnaProc.New(
    function() {
      return !!_.find(cycleAnaTypes, CycleAnaTypesValue[0])
    },
    function() {
      return !!_.find(cycleAnaTypes, CycleAnaTypesValue[1])
    },
    function() {
      return !!_.find(cycleAnaTypes, CycleAnaTypesValue[2])
    },
    function() {
      return {
        Name: FontName,
        Size: 11 + Number(CHART_FONTS[fontType]),
        Type: {
          $: 1,
        },
      }
    }
  )
  const _tProc = window.Trading.Chart.Tenkan.TenkanProc.New(
    function(sNode: any) {
      return window.Trading.Chart.Utils.toChProcSer(sNode)
    },
    function(d: any) {
      return window.Trading.Chart.ProcessorBase.chHighF(d)
    },
    function(d: any) {
      return window.Trading.Chart.ProcessorBase.chLowF(d)
    },
    function(d: any) {
      return window.Trading.Chart.ProcessorBase.chTimeF(d)
    },
    function() {
      return tenkanKikan
    },
    function() {
      return 0.5
    }
  )
  const _pvProc: any = window.Trading.Chart.PriceVolume.PriceVolumeProc.New(
    () => {
      return priceVolumeParam.Ratio
    },
    function() {
      return priceVolumeParam.MaxDisp
    },
    function() {
      let ratio = priceVolumeParam.DispWidthRatio
      if (CHART_FONTS[fontType] === 3) ratio = ratio - 0.04
      if (!priceVolumeParam.MaxDisp) ratio = priceVolumeParam.DispWidthRatio

      return ratio
    },
    function() {
      return {
        Name: FontName,
        Size: 11 + Number(CHART_FONTS[fontType]),
        Type: {
          $: 1,
        },
      }
    }
  )

  let _madoProc = window.Trading.Chart.Mado.MadoProc.New(function() {
    return {
      Name: FontName,
      Size: 11 + Number(CHART_FONTS[fontType]),
      Type: {
        $: 1,
      },
    }
  })
  const [font] = useState({
    IndiTitleFont: {
      Name: FontName,
      Size: 13 + Number(CHART_FONTS[fontType]),
      Type: {
        $: 1,
      },
    },
    CrossCursorFont: {
      Name: FontName,
      Size: 13 + Number(CHART_FONTS[fontType]),
      Type: {
        $: 1,
      },
    },
    ReadVFont: {
      Name: FontName,
      Size: 18 + Number(CHART_FONTS[fontType]),
      Type: {
        $: 1,
      },
    },
    CurPriceFont: {
      Name: FontName,
      Size: 13 + Number(CHART_FONTS[fontType]),
      Type: {
        $: 1,
      },
    },
    TenkanFont: {
      Name: FontName,
      Size: 13 + Number(CHART_FONTS[fontType]),
      Type: {
        $: 1,
      },
    },
  })

  const createCprovider = (priorityFlag?: boolean): void => {
    const _provider = new ChartProvider(setPriceInfo, null, priorityFlag)
    _provider.isMounted = true
    const canvas = document.getElementById(props.canvasId)
    if (!canvas) return
    const r = window.Logic.ChartMain.createChartCanvas(
      canvas,
      _provider,
      2.0,
      () => {
        return lastCloseController.get()
      },
      { $: 1, $0: _tProc },
      font,
      _cProc,
      _pvProc,
      _madoProc,
      { $: 1, $0: setChartInfo }
    )

    const _controller = r.Controller
    const _resetCtx = r.ResetContext
    _controller.Chart.set_UseTenkan(false)
    _controller.Chart.set_UseSimpleDraw(true)
    _controller.SetSize.call(null, [width, height])
    window.Trading.Chart.DrawingItemLib.margin = () => 20 //操作点の大きさ(図形移動などの判定する範囲)
    _controller.Chart.set_BarNumMargin([renderCount * 0.5, 5]) // 表示本数指定(本数を半分近くにすると、表示本数が一致する)
    const _setKVOnOff = setKVOnOff(_controller)
    _setKVOnOff("useReadValueDialog", true)

    dispatch(
      changeProviders(props.canvasId, [
        _provider,
        _controller,
        {
          _cProc: _cProc,
          _pvProc: _pvProc,
          _tProc: _tProc,
          _madoProc: _madoProc,
        },
        _resetCtx,
      ])
    )
  }

  // const startLoading = (requests: chartApiParamObj): void => {
  //   const { Count } = requests
  //   if (!Count || requests) return
  //   if (!props.startSymbol) return
  //   window.spinnerService.show(SPINNER_NAME.tech_chart)
  // }

  // const endLoading = (requests: chartApiParamObj) => {
  //   const { Count } = requests
  //   if (!Count || !requests) return
  //   if (!props.lastSymbol) return
  //   window.spinnerService.hide(SPINNER_NAME.tech_chart)
  // }

  const setPriceInfo = (
    // Scode: string,
    // SecurityType: number,
    // Exchange: number,
    SymbolNameS: string,
    Price: number,
    Close: number,
    Ptime: string,
    LastClose: number,
    ChangedRatio: number,
    ChangedPrice: number,
    Ticker: string,
    PriceCompare: string,
    LastCloseTime: string,
    HighPrice: number,
    HighPriceTime: string,
    OpenPrice: number,
    OpenPriceTime: string,
    LowPrice: number,
    LowPriceTime: string
  ): void => {
    // if (
    //   symbol.Symbol !== Scode ||
    //   symbol.SecurityType !== SecurityType ||
    //   symbol.Exchange !== Exchange
    // )
    //   return
    let price = Price
    // if (price === undefined || price === null) price = Close // 現値が未定義の場合は終値を設定する
    let updateSymbol = _.merge({}, _selectedSymbol.getSymbol())
    if (price !== undefined && price !== null) updateSymbol.Price = price // 値のある場合だけ設定する
    updateSymbol.SymbolNameS = SymbolNameS
    if (LastClose !== undefined && LastClose !== null)
      updateSymbol.LastClose = LastClose
    if (ChangedRatio !== undefined && ChangedRatio !== null)
      updateSymbol.ChangedRatio = ChangedRatio
    if (ChangedPrice !== undefined && ChangedPrice !== null)
      updateSymbol.ChangedPrice = ChangedPrice
    // 矢印設定
    if (prevPrice || prevPrice === 0) {
      if (price > prevPrice) {
        updateSymbol.PriceCompareText = "↑"
      } else if (price < prevPrice) {
        updateSymbol.PriceCompareText = "↓"
      }
    } else {
      updateSymbol.PriceCompareText = ""
    }
    updateSymbol.PriceTime = Ptime
    updateSymbol.Ticker = Ticker
    updateSymbol.PriceCompare = PriceCompare
    updateSymbol.LastCloseTime = LastCloseTime
    updateSymbol.HighPrice = HighPrice
    updateSymbol.HighPriceTime = HighPriceTime
    updateSymbol.OpenPrice = OpenPrice
    updateSymbol.OpenPriceTime = OpenPriceTime
    updateSymbol.LowPrice = LowPrice
    updateSymbol.LowPriceTime = LowPriceTime
    setPrevPrice(updateSymbol.Price) // 次の現値との比較の為、現是を設定する
    dispatch(changeSymbolDetail(props.canvasId, updateSymbol))
  }

  // CProvider第4引数。銘柄入れ替え後の処理。
  // const endChartRequest = () => {
  // }

  const setChartInfo = (infos: any): void => {
    let aInfo = []
    let item = infos.$0 // 線種名,開始位置,値,位置,Y方向位置,色
    let next = null
    if (item) {
      aInfo.push(item)
      next = infos.$1
    }
    while (next) {
      item = next.$0
      if (item) {
        aInfo.push(item)
        next = next.$1
      } else {
        next = null
      }
    }
    const isValid = (s: Array<any>) => {
      if (s.length) return s[0] !== ""

      return false
    }
    const _chartInfo = createChartInfo(aInfo)
    isInfoValid = isValid(aInfo.length ? aInfo[0] : [])
    dispatch(changeDialog(canvasId, _chartInfo))
  }

  const createChartInfo = (aInfo: any) => {
    let _data: any = []
    _.forEach(aInfo, (items: Array<string | object>, index: number) => {
      _data.push([items[0], items[2], items[5]])
    })
    return _data
  }

  useEffect(() => {
    if (!_controller) return
    window.Logic.ChartMain.resetViewPropF(
      _controller.Chart,
      function() {
        return lastCloseController.get()
      },
      { $: 1, $0: _tProc },
      font,
      _cProc,
      _pvProc,
      _madoProc,
      { $: 1, $0: setChartInfo }
    )
    _controller.Chart.Draw()
    setTools(tools, plotType.key, _controller)
    dispatch(
      changeProviders(props.canvasId, [
        _provider,
        _controller,
        {
          _cProc: _cProc,
          _pvProc: _pvProc,
          _tProc: _tProc,
          _madoProc: _madoProc,
        },
        _resetCtx,
      ])
    )
  }, [priceVolumeParam, cycleAnaTypes, tenkanKikan])

  useEffect(() => {
    lastCloseController.price = _.get(symbolDetail, "LastClose", 0)
  }, [symbolDetail])

  useEffect(() => {
    // 数値読み取りの、価格帯出来高のデータ作成。
    setTimeout(() => {
      if (!_procs) return
      let vols = _procs._pvProc._vols
      let valWidth = ""
      let volume = "0"
      let maxValue = 0
      let color = _.get(colorSetting, ["addon", "PriceVolume", "PriceVolume"])
      let col2 = _.get(colorSetting, ["addon", "PriceVolume", "PvMaxVolume"])
      const _priceVolume = !!_.find(tools, _.pick(TOOLS[5], pickTarget))

      if (_priceVolume && mouseValue) {
        if (vols && vols.length) {
          maxValue = Number(vols[0][1])
          let ds =
            vols.length > 1 ? Number(vols[1][0]) - Number(vols[0][0]) : 0.0
          for (let i = 0; i < vols.length - 1; i++) {
            if (maxValue < Number(vols[i + 1][1]))
              maxValue = Number(vols[i + 1][1])
            if (
              mouseValue >= Number(vols[i][0]) &&
              mouseValue < Number(vols[i + 1][0])
            ) {
              valWidth = vols[i][0] + "-" + vols[i + 1][0]
              volume = vols[i + 1][1]
            } else if (i === 0) {
              // 一番下の棒グラフ内のデータか？
              let lowValue = Number(vols[i][0]) - ds
              if (mouseValue >= lowValue && mouseValue < Number(vols[0][0])) {
                valWidth = lowValue + "-" + vols[0][0]
                volume = vols[0][1]
              }
            }
          }
        }
        const _volume = Number(volume)
        if (maxValue <= _volume) color = col2
      }
      const _data = {
        width: valWidth,
        value: volume,
        color: color,
      }
      dispatch(changeDialoPriceVolumeValue(canvasId, _data))
    }, 100)
  }, [mouseValue])

  const unmount = () => {
    if (!_provider) return
    _provider.UnMount()
  }

  useEffect(() => {
    // _providerをサブスクライブしていないと、unmount時にundefindになる。
    return () => unmount()
  }, [_provider])

  useEffect(() => {
    return () => {
      if (canvasId !== MODAL_CANVAS_ID) dispatch(chartUnmount(canvasId))
    }
  }, [])

  useEffect(() => {
    _selectedSymbol.setSymbol(symbol)
  }, [symbol])

  return [createCprovider, cursor]
}

export default useCprovider
